import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import PlaceIcon from '@mui/icons-material/Place'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import BuildIcon from '@mui/icons-material/Build'
import FlagIcon from '@mui/icons-material/Flag'
import TripOriginIcon from '@mui/icons-material/TripOrigin'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import { apiBaseUrl } from '@/constants/apiBaseUrl'

type PublicDeliveryLocation = {
  id: string
  kind: 'pickup' | 'repair'
  label: string
  address: string
  city: string
  state: string
  zip: string
}

type PublicShipmentTracking = {
  plan_id: string
  vehicle: { label: string; vin_last4: string }
  truck_status: string
  current_location: PublicDeliveryLocation | null
  route: PublicDeliveryLocation[]
}

type DeliveryTrackingResponse = {
  client: { display_name: string }
  shipments: PublicShipmentTracking[]
}

async function fetchDeliveryTracking(token: string): Promise<DeliveryTrackingResponse> {
  const url = `${apiBaseUrl}/vehicle-uploader/public/delivery-tracking/${encodeURIComponent(token)}`
  const res = await fetch(url)
  if (res.status === 404) {
    throw new Error('not_found')
  }
  if (!res.ok) {
    throw new Error('request_failed')
  }
  return res.json() as Promise<DeliveryTrackingResponse>
}

function formatCityStateZip(loc: PublicDeliveryLocation): string {
  const cityState = [loc.city?.trim(), loc.state?.trim()].filter(Boolean).join(', ')
  const zip = loc.zip?.trim()
  if (cityState && zip) return `${cityState} ${zip}`
  return cityState || zip || ''
}

function formatTruckStatus(status: string): string {
  return status
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ')
}

function statusColor(status: string): 'default' | 'info' | 'warning' | 'success' {
  const s = status.toLowerCase()
  if (s.includes('deliver') || s.includes('complete') || s.includes('done')) return 'success'
  if (s.includes('transit') || s.includes('route') || s.includes('way') || s.includes('moving')) return 'info'
  if (s.includes('repair') || s.includes('shop') || s.includes('hold') || s.includes('wait')) return 'warning'
  return 'default'
}

function getNonRepairIndices(route: PublicDeliveryLocation[]): number[] {
  return route.map((loc, i) => (loc.kind !== 'repair' ? i : -1)).filter((i): i is number => i >= 0)
}

type StopRole = 'pickup' | 'dropoff' | 'waypoint' | 'repair'

function getRouteStopMeta(
  route: PublicDeliveryLocation[],
  index: number,
  loc: PublicDeliveryLocation
): { title: string; role: StopRole } {
  const nonRepair = getNonRepairIndices(route)
  const pickupIdx = nonRepair.length > 0 ? nonRepair[0] : -1
  const dropoffIdx = nonRepair.length > 0 ? nonRepair[nonRepair.length - 1] : -1

  if (loc.kind !== 'repair') {
    if (nonRepair.length === 1) {
      const onlyIdx = nonRepair[0]
      if (index === onlyIdx) {
        if (onlyIdx === 0) {
          return { title: 'Pickup', role: 'pickup' }
        }
        if (onlyIdx === route.length - 1) {
          return { title: 'Dropoff', role: 'dropoff' }
        }
        return { title: `Route stop ${index + 1}`, role: 'waypoint' }
      }
    }
    if (index === pickupIdx) return { title: 'Pickup', role: 'pickup' }
    if (index === dropoffIdx) return { title: 'Dropoff', role: 'dropoff' }
    return { title: `Route stop ${index + 1}`, role: 'waypoint' }
  }

  return { title: 'Repair', role: 'repair' }
}

function routeMetaForShipment(route: PublicDeliveryLocation[]) {
  return route.map((loc, index) => getRouteStopMeta(route, index, loc))
}

/** Pick the icon + accent color for a given stop role. */
function roleVisual(role: StopRole): {
  Icon: typeof PlaceIcon
  color: string
} {
  switch (role) {
    case 'pickup':
      return { Icon: TripOriginIcon, color: 'info.main' }
    case 'dropoff':
      return { Icon: FlagIcon, color: 'success.main' }
    case 'repair':
      return { Icon: BuildIcon, color: 'warning.main' }
    default:
      return { Icon: PlaceIcon, color: 'text.secondary' }
  }
}

/* -------------------------------------------------------------------------- */
/* Current location banner                                                    */
/* -------------------------------------------------------------------------- */

const CurrentLocationBanner: React.FC<{ loc: PublicDeliveryLocation | null }> = ({ loc }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'primary.main',
      bgcolor: (t) =>
        t.palette.mode === 'light' ? 'primary.50' : 'rgba(255,255,255,0.04)',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          flexShrink: 0,
        }}
      >
        <MyLocationIcon fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="overline"
          sx={{ color: 'primary.main', fontWeight: 700, lineHeight: 1.2, letterSpacing: 0.8 }}
        >
          Current location
        </Typography>
        {loc ? (
          <>
            <Typography variant="body1" fontWeight={700} sx={{ mt: 0.25 }}>
              {loc.label}
            </Typography>
            {loc.address?.trim() ? (
              <Typography variant="body2" color="text.secondary">
                {loc.address.trim()}
              </Typography>
            ) : null}
            {formatCityStateZip(loc) ? (
              <Typography variant="body2" color="text.secondary">
                {formatCityStateZip(loc)}
              </Typography>
            ) : null}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Location updates are not available for this shipment yet.
          </Typography>
        )}
      </Box>
    </Stack>
  </Box>
)

/* -------------------------------------------------------------------------- */
/* Route timeline                                                             */
/* -------------------------------------------------------------------------- */

const RouteTimelineItem: React.FC<{
  loc: PublicDeliveryLocation
  meta: { title: string; role: StopRole }
  isLast: boolean
  isCurrent: boolean
}> = ({ loc, meta, isLast, isCurrent }) => {
  const { Icon, color } = roleVisual(meta.role)

  return (
    <Stack direction="row" spacing={1.5} alignItems="stretch">
      {/* Rail: icon node + connecting line */}
      <Stack alignItems="center" sx={{ width: 36, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: '2px solid',
            borderColor: isCurrent ? 'primary.main' : color,
            bgcolor: isCurrent ? 'primary.main' : 'background.paper',
            color: isCurrent ? 'primary.contrastText' : color,
            flexShrink: 0,
            zIndex: 1,
          }}
        >
          <Icon sx={{ fontSize: 17 }} />
        </Box>
        {!isLast ? (
          <Box
            sx={{
              flex: 1,
              width: 2,
              minHeight: 16,
              bgcolor: 'divider',
              my: 0.25,
            }}
          />
        ) : null}
      </Stack>

      {/* Stop details */}
      <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 2 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          sx={{ mb: 0.25 }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            {meta.title}
          </Typography>
          {isCurrent ? (
            <Chip
              size="small"
              color="primary"
              label="Truck is here"
              sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
            />
          ) : null}
        </Stack>
        <Typography variant="body2" fontWeight={600}>
          {loc.label}
        </Typography>
        {loc.address?.trim() ? (
          <Typography variant="body2" color="text.secondary">
            {loc.address.trim()}
          </Typography>
        ) : null}
        {formatCityStateZip(loc) ? (
          <Typography variant="body2" color="text.secondary">
            {formatCityStateZip(loc)}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  )
}

/* -------------------------------------------------------------------------- */
/* Shipment card                                                              */
/* -------------------------------------------------------------------------- */

const ShipmentSection: React.FC<{ shipment: PublicShipmentTracking; index: number }> = ({
  shipment,
  index,
}) => {
  const meta = useMemo(() => routeMetaForShipment(shipment.route), [shipment.route])
  const statusChipColor = statusColor(shipment.truck_status)

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 1,
        overflow: 'hidden',
      }}
    >
      {/* Card header */}
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 2,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'action.hover',
                color: 'text.secondary',
                flexShrink: 0,
              }}
            >
              <DirectionsCarIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ lineHeight: 1.2, letterSpacing: 0.8 }}
              >
                Shipment {index + 1}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                {shipment.vehicle.label}
              </Typography>
              {shipment.vehicle.vin_last4 ? (
                <Typography variant="caption" color="text.secondary">
                  VIN ending …{shipment.vehicle.vin_last4}
                </Typography>
              ) : null}
            </Box>
          </Stack>
          <Chip
            size="small"
            label={formatTruckStatus(shipment.truck_status)}
            color={statusChipColor === 'default' ? undefined : statusChipColor}
            sx={{ fontWeight: 700, flexShrink: 0 }}
          />
        </Stack>
      </Box>

      {/* Card body */}
      <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2.5 }}>
        <Stack spacing={2.5}>
          <CurrentLocationBanner loc={shipment.current_location} />

          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 1.5, color: 'text.primary' }}
            >
              Planned route
            </Typography>
            {shipment.route.length === 0 ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No route stops are listed on this shipment plan yet.
                </Typography>
              </Box>
            ) : (
              <Box>
                {shipment.route.map((loc, i) => {
                  const stopMeta =
                    meta[i] ?? {
                      title: `Stop ${i + 1}`,
                      role: (loc.kind === 'repair' ? 'repair' : 'waypoint') as StopRole,
                    }
                  const isCurrent =
                    shipment.current_location != null &&
                    shipment.current_location.id === loc.id
                  return (
                    <RouteTimelineItem
                      key={`${shipment.plan_id}-${loc.id}-${i}`}
                      loc={loc}
                      meta={stopMeta}
                      isLast={i === shipment.route.length - 1}
                      isCurrent={isCurrent}
                    />
                  )
                })}
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    </Paper>
  )
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

const PublicDeliveryTrackingPage: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { token } = useParams<{ token: string }>()
  const safeToken = token?.trim() ?? ''

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['public-delivery-tracking', safeToken],
    queryFn: () => fetchDeliveryTracking(safeToken),
    enabled: safeToken.length > 0,
    retry: false,
  })

  const shipmentCount = data?.shipments.length ?? 0

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.100',
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="md">
        {/* ---------------------------------------------------------------- */}
        {/* Header                                                           */}
        {/* ---------------------------------------------------------------- */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: isMobile ? 2 : 2.5,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: 1,
          }}
        >
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={isMobile ? 1.5 : 2}
            alignItems={isMobile ? 'flex-start' : 'center'}
          >
            <Box
              component="img"
              src="https://firebasestorage.googleapis.com/v0/b/otw-production-91ffb.appspot.com/o/public%2FLogoVariation-OpticTruckWorks-05.png?alt=media&token=c3428c05-276c-4d32-ac33-a7d9d51fe380"
              alt="Optic Truck Works"
              sx={{ width: isMobile ? 150 : 190, height: 'auto' }}
            />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Delivery Tracking
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Follow the live status and planned route of every truck in your delivery.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* ---------------------------------------------------------------- */}
        {/* States                                                          */}
        {/* ---------------------------------------------------------------- */}
        {!safeToken ? (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Missing tracking link. Check the URL you were sent.
          </Alert>
        ) : isLoading ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 1,
              py: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading your delivery status…
            </Typography>
          </Paper>
        ) : isError ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error instanceof Error && error.message === 'not_found'
              ? 'This tracking link is invalid or has expired.'
              : 'We could not load delivery status. Please try again later.'}
          </Alert>
        ) : data ? (
          <Stack spacing={3}>
            {/* Client summary bar */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 1,
                p: { xs: 2, md: 2.5 },
              }}
            >
              <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={isMobile ? 1 : 2}
                alignItems={isMobile ? 'flex-start' : 'center'}
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 0.8 }}
                  >
                    Prepared for
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                    {data.client.display_name}
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    bgcolor: 'action.hover',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 2,
                  }}
                >
                  <LocalShippingIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="body2" fontWeight={700}>
                    {shipmentCount === 1 ? '1 shipment' : `${shipmentCount} shipments`}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            {/* Shipments */}
            {shipmentCount === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 1,
                  py: 6,
                  px: 3,
                  textAlign: 'center',
                }}
              >
                <LocalShippingIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={700}>
                  No shipments yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  No shipment plans are on file for your account yet. Check back soon.
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={2.5}>
                {data.shipments.map((shipment, index) => (
                  <ShipmentSection key={shipment.plan_id} shipment={shipment} index={index} />
                ))}
              </Stack>
            )}

            {/* Footer */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: 'center', display: 'block', pt: 1 }}
            >
              Optic Truck Works · Questions about your delivery? Reach out to your account
              contact.
            </Typography>
          </Stack>
        ) : null}
      </Container>
    </Box>
  )
}

export default PublicDeliveryTrackingPage